import { inject } from '@angular/core';
import type { HttpInterceptorFn } from '@angular/common/http';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AppInsightsService } from './app-insights.service';

/**
 * HTTP Interceptor to track API requests and responses in Application Insights
 * Handles errors gracefully to prevent AppInsights from breaking the application
 */
export const appInsightsHttpInterceptor: HttpInterceptorFn = (req, next) => {
  const appInsights = inject(AppInsightsService);
  const startTime = Date.now();

  return next(req).pipe(
    tap({
      next: (event) => {
        try {
          // Track successful HTTP responses
          if (event.type === 4) { // HttpEventType.Response
            const duration = Date.now() - startTime;
            appInsights.trackMetric('HttpRequestDuration', duration, {
              url: req.url,
              method: req.method,
              status: '200'
            });
          }
        } catch (error) {
          console.error('AppInsights tracking error (success):', error);
          // Swallow error to prevent breaking the app
        }
      },
      error: (error) => {
        try {
          // Track failed HTTP requests
          const duration = Date.now() - startTime;
          appInsights.trackException(error, 2); // Severity: Warning
          appInsights.trackMetric('HttpRequestDuration', duration, {
            url: req.url,
            method: req.method,
            status: error.status?.toString() || 'unknown',
            error: error.message
          });
        } catch (appInsightsError) {
          console.error('AppInsights tracking error (failure):', appInsightsError);
          // Swallow error to prevent breaking the app
        }
      }
    }),
    catchError((error) => {
      // Always pass through the error - this interceptor is for tracking only
      return throwError(() => error);
    })
  );
};
